import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Zap, 
  Bell,
  BellRing,
  Sun,
  CloudRain,
  TrendingUp,
  CreditCard,
  AlertTriangle,
  Check,
  LogOut,
  RefreshCw,
  ChevronRight,
  Clock,
  FileText,
  Download
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Alerts = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [alertsRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/alerts`, { headers }),
        axios.get(`${API_URL}/api/billing/invoices`, { headers })
      ]);
      
      setAlerts(alertsRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAlerts = async () => {
    setGenerating(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API_URL}/api/alerts/generate`, {}, { headers });
      toast.success(`${response.data.length} alerte(s) générée(s)`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const markAsRead = async (alertId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/api/alerts/${alertId}/read`, {}, { headers });
      setAlerts(alerts.map(a => a.id === alertId ? { ...a, read: true } : a));
    } catch (error) {
      console.error('Error marking alert as read:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Déconnexion réussie');
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'weather': return <Sun className="w-5 h-5 text-yellow-500" />;
      case 'price': return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'transaction': return <Zap className="w-5 h-5 text-blue-500" />;
      case 'credit': return <CreditCard className="w-5 h-5 text-red-500" />;
      case 'recommendation': return <BellRing className="w-5 h-5 text-purple-500" />;
      default: return <Bell className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getAlertBg = (type, read) => {
    if (read) return 'bg-muted/30';
    switch (type) {
      case 'weather': return 'bg-yellow-50 border-yellow-200';
      case 'price': return 'bg-green-50 border-green-200';
      case 'transaction': return 'bg-blue-50 border-blue-200';
      case 'credit': return 'bg-red-50 border-red-200';
      default: return 'bg-white';
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high': return <Badge variant="destructive">Urgent</Badge>;
      case 'medium': return <Badge variant="default">Important</Badge>;
      case 'low': return <Badge variant="secondary">Info</Badge>;
      default: return null;
    }
  };

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  const unreadCount = alerts.filter(a => !a.read).length;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="Green Teva" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-bold text-primary">Green Teva</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
              <Link to="/communities" className="nav-link">Communautés</Link>
              <Link to="/forecasts" className="nav-link">Prévisions</Link>
              <Link to="/alerts" className="nav-link active">
                Alertes
                {unreadCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                    {unreadCount}
                  </span>
                )}
              </Link>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-foreground">{user?.name}</p>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2" data-testid="page-title">
                Alertes & Notifications
              </h1>
              <p className="text-muted-foreground">
                {unreadCount > 0 ? `${unreadCount} nouvelle(s) alerte(s)` : 'Toutes vos alertes sont lues'}
              </p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={generateAlerts}
                variant="outline"
                disabled={generating}
                className="gap-2"
                data-testid="generate-alerts-btn"
              >
                {generating ? (
                  <div className="loader w-4 h-4" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Générer alertes
              </Button>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Alerts List */}
            <div className="lg:col-span-2 space-y-4">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Alertes récentes
              </h2>
              
              {alerts.length === 0 ? (
                <Card className="stats-card">
                  <CardContent className="p-8 text-center">
                    <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-semibold text-foreground mb-2">Aucune alerte</h3>
                    <p className="text-muted-foreground mb-4">
                      Cliquez sur "Générer alertes" pour recevoir des recommandations
                    </p>
                  </CardContent>
                </Card>
              ) : (
                alerts.map((alert) => (
                  <Card 
                    key={alert.id}
                    className={`border transition-all cursor-pointer hover:shadow-md ${getAlertBg(alert.alert_type, alert.read)}`}
                    onClick={() => {
                      if (!alert.read) markAsRead(alert.id);
                      if (alert.action_url) navigate(alert.action_url);
                    }}
                    data-testid={`alert-${alert.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="p-2 rounded-full bg-white shadow-sm">
                          {getAlertIcon(alert.alert_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={`font-semibold ${alert.read ? 'text-muted-foreground' : 'text-foreground'}`}>
                              {alert.title}
                            </h3>
                            {getPriorityBadge(alert.priority)}
                            {!alert.read && (
                              <span className="w-2 h-2 rounded-full bg-primary" />
                            )}
                          </div>
                          <p className={`text-sm ${alert.read ? 'text-muted-foreground/70' : 'text-muted-foreground'}`}>
                            {alert.message}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(alert.created_at).toLocaleString('fr-FR')}
                            </span>
                            {alert.action_url && (
                              <span className="text-xs text-primary flex items-center gap-1">
                                Voir détails
                                <ChevronRight className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                        </div>
                        {alert.read && (
                          <Check className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Invoices Sidebar */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Factures
              </h2>
              
              {invoices.length === 0 ? (
                <Card className="stats-card">
                  <CardContent className="p-6 text-center">
                    <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      Aucune facture pour le moment
                    </p>
                  </CardContent>
                </Card>
              ) : (
                invoices.slice(0, 5).map((invoice) => (
                  <Card 
                    key={invoice.id}
                    className="stats-card cursor-pointer hover:border-primary"
                    data-testid={`invoice-${invoice.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-sm text-muted-foreground">
                          {invoice.invoice_number}
                        </span>
                        <Badge 
                          variant={invoice.balance_type === 'credit' ? 'default' : 'secondary'}
                          className={invoice.balance_type === 'credit' ? 'bg-green-500' : 'bg-red-500'}
                        >
                          {invoice.balance_type === 'credit' ? 'Crédit' : 'Débit'}
                        </Badge>
                      </div>
                      <p className="text-xl font-bold text-foreground mb-1">
                        {invoice.balance_type === 'credit' ? '+' : ''}{invoice.total_amount}€
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {invoice.period_start} → {invoice.period_end}
                      </p>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t">
                        <span className="text-xs text-muted-foreground">
                          {invoice.community_name}
                        </span>
                        <Button variant="ghost" size="sm" className="h-7 px-2">
                          <Download className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
              
              {invoices.length > 5 && (
                <Button variant="outline" className="w-full">
                  Voir toutes les factures
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Alerts;
